import { firstValueFrom, Observable, of, Subscription, timer } from "rxjs";
import { catchError, defaultIfEmpty, map, shareReplay, switchMap } from "rxjs/operators";
import { DbService } from "src/app/core/services/db.service";
// models
import { EmployeeMailSignatures } from "src/app/domains/employee_mail_signature";

import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { environment } from "../../../../environments/environment";
import { Employee } from "../domains/employee";
import { EmployeePositionHistory } from "../domains/employee_position_history";
import { HourlyRateEntry, NewHourlyRateEntry } from "../domains/employee-hourly-rate-entry";
import { Salary } from "../domains/salary";

const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

export interface AddEmployeeResult {
  employee: Employee;
  welcomeEmailSent: boolean;
}

export const WELCOME_EMAIL_SKIPPED_WARNING =
  "Mail de bienvenue non envoyé : un compte EVA actif existe déjà pour cette adresse.";

@Injectable({
  providedIn: "root",
})
export class EmployeesService {
  url = environment.apiUrl + "/employees";
  urlSalary = environment.apiUrl + "/salaries";
  private _currentEmployee: Employee = null;
  private _currentEmployee$: Observable<Employee> = null;
  private _refreshSub: Subscription = null;
  private _onRefreshCallback: (employee: Employee) => void = null;
  private employeeNamesCache$: Observable<Map<string, string>> = null;

  /**
   * Retourne une map employee_id → display_name, cachée avec shareReplay(1).
   * Utilise getAllEmployeesLight() pour minimiser la charge réseau.
   */
  getEmployeeNamesMap(): Observable<Map<string, string>> {
    if (!this.employeeNamesCache$) {
      this.employeeNamesCache$ = this.getAllEmployeesLight().pipe(
        map((employees) => {
          const namesMap = new Map<string, string>();
          employees.forEach((e) => {
            namesMap.set(e.employee_id, e.display_name);
            if (e.user_id) {
              namesMap.set(e.user_id, e.display_name);
            }
          });
          return namesMap;
        }),
        shareReplay(1),
      );
    }
    return this.employeeNamesCache$;
  }

  constructor(
    private http: HttpClient,
    private dbService: DbService,
  ) {}

  public getAllEmployees(): Observable<Employee[]> {
    return this.http.get<Employee[]>(this.url).pipe(map(Employee.createInstances));
  }

  public getEmployeeSignatures(): Observable<EmployeeMailSignatures> {
    return this.http
      .get<EmployeeMailSignatures>(this.url + "/mail-signatures")
      .pipe(map(EmployeeMailSignatures.createInstance));
  }

  public editEmployeeSignatures(employeeMailSignatures: EmployeeMailSignatures): Observable<EmployeeMailSignatures> {
    return this.http
      .put<EmployeeMailSignatures>(this.url + "/mail-signatures", employeeMailSignatures)
      .pipe(map(EmployeeMailSignatures.createInstance));
  }

  public getAllEmployeesLight(): Observable<Employee[]> {
    return this.http.get<Employee[]>(this.url + "/all/light").pipe(map(Employee.createInstances));
  }

  public getAllEnabledEmployees(return_type: "LIGHT" | "FULL" = "LIGHT", addIds = []): Observable<Employee[]> {
    return this.http
      .get<Employee[]>(this.url + "/enabled", {
        params: { return_type: return_type, add_ids: addIds.join(",") },
      })
      .pipe(
        map(Employee.createInstances),
        catchError(() => of([])),
      );
  }

  /** Positions réduites aux heures contractuelles, servies à tout rôle, indexées par employee_id. */
  public getPlanningContractHoursPositions(): Observable<Map<string, EmployeePositionHistory>> {
    return this.http.get<Employee[]>(this.url + "/enabled", { params: { return_type: "PLANNING_LIGHT" } }).pipe(
      map(
        (employees) =>
          new Map(
            employees
              .filter((e) => e.employee_position_current)
              .map((e) => [e.employee_id, EmployeePositionHistory.createInstance(e.employee_position_current)]),
          ),
      ),
      catchError(() => of(new Map<string, EmployeePositionHistory>())),
    );
  }

  public getEmployee(id: string): Observable<Employee> {
    return this.http.get<Employee>(this.url + "/" + id).pipe(map(Employee.createInstance));
  }

  public getMyProfile(id: string): Observable<Employee> {
    return this.http.get<Employee>(this.url + "/" + id + "/my-profile").pipe(map(Employee.createInstance));
  }

  public getEmployeeFull(id: string): Observable<Employee> {
    return this.http.get<Employee>(this.url + "/full/" + id).pipe(map(Employee.createInstance));
  }

  public async currentEmployeeAsync(): Promise<Employee> {
    if (this._currentEmployee) return this._currentEmployee;
    this._currentEmployee = await this.getCurrentEmployee().toPromise();
    return this._currentEmployee;
  }

  public getCurrentEmployee(fromCache = true): Observable<Employee> {
    if (fromCache && this._currentEmployee) {
      return of(this._currentEmployee);
    }

    if (!this._currentEmployee$) {
      this._currentEmployee$ = this.http.get<Employee>(this.url + "/me").pipe(
        map((employee) => {
          this._currentEmployee = Employee.createInstance(employee);
          this._currentEmployee$ = null;
          this.startAutoRefresh();
          return this._currentEmployee;
        }),
        shareReplay(1),
      );
    }

    return this._currentEmployee$;
  }

  public onCurrentEmployeeRefresh(callback: (employee: Employee) => void): void {
    this._onRefreshCallback = callback;
  }

  private startAutoRefresh(): void {
    if (this._refreshSub) return;

    this._refreshSub = timer(REFRESH_INTERVAL_MS, REFRESH_INTERVAL_MS)
      .pipe(switchMap(() => this.http.get<Employee>(this.url + "/me")))
      .subscribe((employee) => {
        this._currentEmployee = Employee.createInstance(employee);
        this._onRefreshCallback?.(this._currentEmployee);
      });
  }

  clearSession(): void {
    this._refreshSub?.unsubscribe();
    this._refreshSub = null;
    this._currentEmployee = null;
    this._currentEmployee$ = null;
    this._onRefreshCallback = null;
  }

  public getAvatar(id: string): Observable<any> {
    return this.http.get(this.url + "/" + id + "/avatar").pipe(shareReplay(1));
  }

  async getMeAvatar(): Promise<string> {
    const storedAvatar = await firstValueFrom(this.dbService.getAvatar());
    if (storedAvatar === null) {
      const employee = await firstValueFrom(this.getCurrentEmployee().pipe(defaultIfEmpty(null)));
      if (!employee) {
        return "";
      }
      const avatar = await firstValueFrom(this.http.get<string>(`${this.url}/${employee.employee_id}/avatar`));
      try {
        await this.dbService.saveAvatar(avatar);
      } catch {
        // IndexedDB indisponible — l'avatar sera re-fetché au prochain chargement
      }
      return avatar;
    }
    return storedAvatar;
  }

  public addEmployee(employee: Employee, sendWelcomeEmail = true): Observable<AddEmployeeResult> {
    const payload = {
      ...employee,
      send_welcome_email: sendWelcomeEmail === true,
    };
    return this.http.post<Employee & { welcome_email_sent?: boolean }>(this.url, payload).pipe(
      map(({ welcome_email_sent, ...employeeJson }) => ({
        employee: Employee.createInstance(employeeJson),
        // Back non déployé (clé absente) : on ne prétend pas que le mail a été bloqué.
        welcomeEmailSent: welcome_email_sent !== false,
      })),
    );
  }

  public sendNewEmployeeMail(employee: Employee): Observable<Employee> {
    return this.http
      .post<Employee>(this.url + "/" + employee.employee_id + "/send-mail-creation", {})
      .pipe(map(Employee.createInstance));
  }

  public editEmployee(
    employee: Employee,
    external_data = false,
    year_start = 0,
    year_end = 0,
    start_month = 0,
    end_month = 0,
  ): Observable<Employee> {
    return this.http
      .put<Employee>(
        this.url +
          "/" +
          employee.employee_id +
          "?external_data=" +
          external_data.toString() +
          "&year_start=" +
          year_start.toString() +
          "&year_end=" +
          year_end.toString() +
          "&start_month=" +
          start_month.toString() +
          "&end_month=" +
          end_month.toString(),
        employee,
      )
      .pipe(map(Employee.createInstance));
  }
  public editMyProfile(employee: Employee): Observable<Employee> {
    return this.http
      .put<Employee>(this.url + "/" + employee.employee_id + "/my-profile", employee)
      .pipe(map(Employee.createInstance));
  }

  public disableEmployee(
    employee_id: string,
    external_data = false,
    delete_future_activities = false,
  ): Observable<Employee> {
    return this.http
      .put<Employee>(
        this.url +
          "/" +
          employee_id +
          "/disable" +
          "?external_data=" +
          external_data.toString() +
          "&delete_future_activities=" +
          delete_future_activities.toString(),
        {},
      )
      .pipe(map(Employee.createInstance));
  }

  public updateWorkingActivities(employee_id: string): Observable<Employee> {
    return this.http
      .put<Employee>(this.url + "/" + employee_id + "/update-working-activities", {})
      .pipe(map(Employee.createInstance));
  }

  public updateSendNotificationPlanning(employee_id: string, send_notif_planning: Boolean): Observable<any> {
    return this.http.patch<any>(this.url + "/" + employee_id + "/send_notif_planning", {
      send_notif_planning,
    });
  }

  /** Accès d'un collaborateur à l'assistant IA (admin seulement). */
  public updateAiAssistantAccess(employee_id: string, enabled: boolean): Observable<{ ai_assistant_enabled: boolean }> {
    return this.http.patch<{ ai_assistant_enabled: boolean }>(`${this.url}/${employee_id}/ai-assistant`, { enabled });
  }

  retrocativeUpdateHourCost(employee_id, position): Observable<Employee> {
    return this.http
      .put<Employee>(this.url + "/" + employee_id + "/retrocative-update-hour-cost", position)
      .pipe(map(Employee.createInstance));
  }

  //Salaries

  public addSalary(salary: Salary): Observable<Salary> {
    return this.http.post<Salary>(this.urlSalary, salary).pipe(map(Salary.createInstance));
  }

  public createYearSalary(year: number): Observable<Salary[]> {
    return this.http.post<Salary[]>(this.urlSalary + "/generate/" + year, year).pipe(map(Salary.createInstances));
  }

  public createYearSalarySuperadmin(year: number): Observable<Salary[]> {
    return this.http.post<Salary[]>(this.urlSalary + "/superadmin/generate/" + year, year);
  }

  public editSalary(salary: Salary): Observable<Salary> {
    return this.http.put<Salary>(this.urlSalary + "/" + salary.salary_id, salary).pipe(map(Salary.createInstance));
  }

  public getSalary(id: string): Observable<Salary> {
    return this.http.get<Salary>(this.urlSalary + "/" + id).pipe(map(Salary.createInstance));
  }

  public getEmployeeSalaries(
    id: string,
    start_date = null,
    end_date = null,
    filter_status = null,
  ): Observable<Salary[]> {
    let params = new HttpParams();
    if (start_date) {
      params = params.append("start_date", start_date);
    }
    if (end_date) {
      params = params.append("end_date", end_date);
    }
    if (filter_status) {
      params = params.append("filter_status", filter_status);
    }
    return this.http
      .get<Salary[]>(this.urlSalary + "/employee/" + id, { params: params })
      .pipe(map(Salary.createInstances));
  }

  public getYearSalaries(year: number): Observable<Salary[]> {
    return this.http.get<Salary[]>(this.urlSalary + "/year/" + year).pipe(map(Salary.createInstances));
  }

  public getAllSalaries(): Observable<Salary[]> {
    return this.http.get<Salary[]>(this.urlSalary).pipe(map(Salary.createInstances));
  }

  public getAllSalariesToPay(): Observable<Salary[]> {
    return this.http.get<Salary[]>(this.urlSalary + "/to-pay").pipe(map(Salary.createInstances));
  }

  public addYearMonthEmployeeSalaries(
    id: string,
    year: number,
    start_month: number = 0,
    end_month: number = 0,
  ): Observable<Salary[]> {
    return this.http
      .post<Salary[]>(this.urlSalary + "/" + id + "/" + year, [new Salary()])
      .pipe(map(Salary.createInstances));
  }

  // Saisies en masse des taux horaires (Paramètres collaborateurs > Taux horaires)
  public getHourlyRateEntries(): Observable<HourlyRateEntry[]> {
    return this.http.get<HourlyRateEntry[]>(environment.apiUrl + "/hourly-rate-entries");
  }

  public addHourlyRateEntry(entry: NewHourlyRateEntry): Observable<HourlyRateEntry> {
    return this.http.post<HourlyRateEntry>(environment.apiUrl + "/hourly-rate-entries", entry);
  }

  public disableHourlyRateEntry(entryId: string): Observable<HourlyRateEntry> {
    return this.http.put<HourlyRateEntry>(environment.apiUrl + `/hourly-rate-entries/${entryId}/disable`, {});
  }

  public changeEmail(employeeId: string, newEmail: string): Observable<any> {
    return this.http.post<any>(this.url + "/" + employeeId + "/change-email", { new_email: newEmail });
  }

  public changePassword(employeeId: string, newPassword: string): Observable<any> {
    return this.http.post<any>(this.url + "/" + employeeId + "/change-password", { new_password: newPassword });
  }

  public getCorporateIds() {
    return this.http.get<
      Array<{
        employee_id: string;
        first_name: string;
        last_name: string;
        employee_email: string;
        employee_corporate_id: string;
      }>
    >(`${this.url}/corporate-ids`);
  }
}
